import { initReactI18next } from 'react-i18next'
import i18next from 'i18next'
import en from './locales/en.json'

export const SUPPORTED_LOCALES = ['en', 'de', 'fr', 'it', 'es', 'nl', 'ja', 'zh-CN', 'zh-TW'] as const
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number]

const DEFAULT_LOCALE: SupportedLocale = 'en'

// Chinese script (CN/TW) is significant; all other region subtags are dropped.
// Unspecified Mandarin regions (zh-MY, zh-SG, bare zh-XX) default to Simplified.
export function normalizeLocale(locale?: string | null): SupportedLocale {
  if (!locale) return DEFAULT_LOCALE
  const lower = locale.toLowerCase()
  if (lower.startsWith('zh-hans') || lower === 'zh-cn' || lower === 'zh') return 'zh-CN'
  if (lower.startsWith('zh-hant') || lower === 'zh-tw' || lower === 'zh-hk' || lower === 'zh-mo') return 'zh-TW'
  const base = lower.split('-')[0]
  if (base === 'zh') return 'zh-CN'
  const match = SUPPORTED_LOCALES.find(l => l === base)
  return match ?? DEFAULT_LOCALE
}

// Read the persisted locale before React mounts so i18next initializes with
// the right language and the first paint doesn't flash English. Falls back to
// navigator.language for first-time visitors.
export function detectInitialLocale(): SupportedLocale {
  try {
    const persisted = typeof localStorage !== 'undefined' ? localStorage.getItem('persist:root') : null
    if (persisted) {
      const root = JSON.parse(persisted) as Record<string, string>
      if (root.locale) {
        const slice = JSON.parse(root.locale) as { locale?: string }
        if (slice.locale) return normalizeLocale(slice.locale)
      }
    }
  } catch {
    // fall through to navigator detection
  }
  if (typeof navigator !== 'undefined') return normalizeLocale(navigator.language)
  return DEFAULT_LOCALE
}

const LOCALE_LOADERS: Record<Exclude<SupportedLocale, 'en'>, () => Promise<{ default: object }>> = {
  'de': () => import('./locales/de.json'),
  'fr': () => import('./locales/fr.json'),
  'it': () => import('./locales/it.json'),
  'es': () => import('./locales/es.json'),
  'nl': () => import('./locales/nl.json'),
  'ja': () => import('./locales/ja.json'),
  'zh-CN': () => import('./locales/zh-CN.json'),
  'zh-TW': () => import('./locales/zh-TW.json'),
}

const loadedLocales = new Set<SupportedLocale>(['en'])
const inFlightLoaders = new Map<SupportedLocale, Promise<void>>()

export async function ensureLocaleLoaded(locale: SupportedLocale): Promise<void> {
  if (loadedLocales.has(locale)) return Promise.resolve()
  const existing = inFlightLoaders.get(locale)
  if (existing) return existing
  const loader = LOCALE_LOADERS[locale as Exclude<SupportedLocale, 'en'>]
  if (!loader) return Promise.resolve()
  const promise = loader()
    .then(mod => {
      agentDashboardI18n.addResourceBundle(locale, 'translation', mod.default, true, true)
      loadedLocales.add(locale)
    })
    .finally(() => {
      inFlightLoaders.delete(locale)
    })
  inFlightLoaders.set(locale, promise)
  return promise
}

const initialLocale = detectInitialLocale()

export const agentDashboardI18n = i18next.createInstance()

void agentDashboardI18n.use(initReactI18next).init({
  resources: { en: { translation: en } },
  lng: initialLocale,
  fallbackLng: DEFAULT_LOCALE,
  interpolation: { escapeValue: false },
  returnNull: false,
})

agentDashboardI18n.services.formatter?.add('lowercase', (value, lng) => typeof value === 'string' ? value.toLocaleLowerCase(lng) : value)

// A screen reader picks its pronunciation from the document language, so it has to follow
// the catalog rather than stay on the `en` index.html hardcodes. Setting it optimistically
// is safe: a failed catalog load ends in changeLanguage('en'), which fires this again.
function syncDocumentLanguage(language: string): void {
  if (typeof document !== 'undefined') {
    document.documentElement.lang = language
  }
}

agentDashboardI18n.on('languageChanged', syncDocumentLanguage)
syncDocumentLanguage(initialLocale)

if (initialLocale !== 'en') {
  void ensureLocaleLoaded(initialLocale)
    .then(() => {
      void agentDashboardI18n.changeLanguage(initialLocale)
    })
    // console.warn rather than the structured logger — Sentry isn't
    // initialized at module-eval time.
    .catch(err => {
      console.warn(`Failed to load locale "${initialLocale}", falling back to en:`, err)
      void agentDashboardI18n.changeLanguage('en')
    })
}
